import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { useEffect, useRef, useState } from "react";
import {
	fetchQuery,
	graphql,
	useLazyLoadQuery,
	useMutation,
	useRelayEnvironment,
} from "react-relay";
import { toast } from "sonner";
import { Heading, Subheading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	isKnownPaymentProvider,
	PaymentProviderInfo,
} from "@/utils/payment-provider-info";
import type { integracoesCheckoutIdQuery } from "./__generated__/integracoesCheckoutIdQuery.graphql";
import type { integracoesCheckoutIdUpdateProductMutation } from "./__generated__/integracoesCheckoutIdUpdateProductMutation.graphql";

const checkoutIdQuery = graphql`
	query integracoesCheckoutIdQuery($id: ID!) {
		node(id: $id) {
			... on PaymentIntegration {
				id
				hash
				provider
				tested
				active
				products {
					id
					enabled
					productName
					productId
					accessGroup {
						accessGroup {
							courses {
								course {
									name
								}
							}
							spaces {
								name
								icon
							}
						}
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/integracoes/checkout/$id/",
)({
	component: RouteComponent,
});

type ProductTableDef = {
	id: string;
	enabled: boolean;
	productName: string;
	productId: string;
	courses: {
		name: string;
	}[];
	spaces: {
		name: string;
		icon: string;
	}[];
};

function ProductActions({ id }: { id: string }) {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const navigate = useNavigate();

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon">
					<Icon name="EllipsisVertical" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuItem
					onClick={() =>
						navigate({
							to: "/c/$club/configuracoes/integracoes/checkout/produto/$id",
							params: { club: club.slug, id },
						})
					}
				>
					<Icon name="Pencil" />
					Editar
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function ProductSwitch({ id, active }: { id: string; active: boolean }) {
	const [mutate, isPending] =
		useMutation<integracoesCheckoutIdUpdateProductMutation>(
			graphql`
      mutation integracoesCheckoutIdUpdateProductMutation($payload: PaymentIntegrationProductUpdateInput!) {
        paymentIntegrationProductUpdate(paymentIntegrationProductInput: $payload) {
          id
          enabled
        }
      }
     `,
		);

	return (
		<Switch
			disabled={isPending}
			checked={active}
			onCheckedChange={(val: boolean) =>
				mutate({
					variables: {
						payload: {
							id,
							enabled: val,
						},
					},
					optimisticResponse: {
						paymentIntegrationProductUpdate: {
							id,
							enabled: val,
						},
					},
					onCompleted: (_response, errors) => {
						if (errors?.length) toast.error(errors[0].message);
					},
					onError: () => {
						toast.error("Erro ao atualizar o produto.");
					},
				})
			}
		/>
	);
}

const columns: ColumnDef<ProductTableDef>[] = [
	{
		accessorKey: "enabled",
		header: "",
		cell: ({ row, getValue }) => (
			<ProductSwitch id={row.original.id} active={getValue<boolean>()} />
		),
		maxSize: 48,
	},
	{
		accessorKey: "productName",
		header: "Produto",
	},
	{
		accessorKey: "productId",
		header: "ID do Produto",
	},
	{
		accessorKey: "courses",
		header: "Cursos",
		cell: ({ getValue }) =>
			getValue<ProductTableDef["courses"]>().length > 0
				? getValue<ProductTableDef["courses"]>()[0].name +
					(getValue<ProductTableDef["courses"]>().length > 1
						? ` (+${getValue<ProductTableDef["courses"]>().length - 1})`
						: "")
				: "-",
	},
	{
		accessorKey: "spaces",
		header: "Feeds",
		cell: ({ getValue }) =>
			getValue<ProductTableDef["spaces"]>().length > 0
				? getValue<ProductTableDef["spaces"]>()[0].name +
					(getValue<ProductTableDef["spaces"]>().length > 1
						? ` (+${getValue<ProductTableDef["spaces"]>().length - 1})`
						: "")
				: "-",
	},
	{
		id: "actions",
		header: "",
		maxSize: 48,
		cell: ({ row }) => <ProductActions id={row.original.id} />,
	},
];

function ProductsTable({
	data,
	isPending,
}: {
	data: ProductTableDef[];
	isPending: boolean;
}) {
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	function renderBody() {
		if (isPending) {
			return Array.from({ length: 5 }, (_, index) => index).map((i) =>
				table.getHeaderGroups().map((headerGroup) => (
					<TableRow key={`${headerGroup.id}-${i}`}>
						{headerGroup.headers.map((header) => {
							return (
								<TableCell
									key={header.id}
									style={{
										width: header.column.columnDef.size,
										maxWidth: header.column.columnDef.maxSize,
									}}
								>
									<Skeleton className="h-5 w-32 bg-muted" />
								</TableCell>
							);
						})}
					</TableRow>
				)),
			);
		}

		if (!table.getRowModel().rows.length) {
			return (
				<TableRow>
					<TableCell colSpan={columns.length} className="h-24 text-center">
						Nenhum produto cadastrado.
					</TableCell>
				</TableRow>
			);
		}

		return table.getRowModel().rows.map((row) => (
			<TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
				{row.getVisibleCells().map((cell) => (
					<TableCell
						key={cell.id}
						style={{
							width: cell.column.columnDef.size,
							maxWidth: cell.column.columnDef.maxSize,
						}}
					>
						{flexRender(cell.column.columnDef.cell, cell.getContext())}
					</TableCell>
				))}
			</TableRow>
		));
	}

	return (
		<div className="overflow-hidden rounded-md border">
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								return (
									<TableHead
										key={header.id}
										style={{
											width: header.column.columnDef.size,
											maxWidth: header.column.columnDef.maxSize,
										}}
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>{renderBody()}</TableBody>
			</Table>
		</div>
	);
}

function RouteComponent() {
	const { id } = useParams({ from: Route.id });
	const { club } = useRouteContext({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const [, copyToClipboard] = useCopyToClipboard();
	const environment = useRelayEnvironment();
	const pollIntervalRef = useRef<number | null>(null);
	const [isPolling, setIsPolling] = useState(false);

	const data = useLazyLoadQuery<integracoesCheckoutIdQuery>(checkoutIdQuery, {
		id,
	});

	const integration = data.node;

	// Poll for webhook test status when not yet tested
	useEffect(() => {
		if (integration?.tested === true) return;

		setIsPolling(true);

		pollIntervalRef.current = window.setInterval(() => {
			fetchQuery<integracoesCheckoutIdQuery>(
				environment,
				checkoutIdQuery,
				{ id },
				{ fetchPolicy: "network-only" },
			).subscribe({
				next: (result) => {
					if (result.node?.tested === true) {
						toast.success("Webhook validado com sucesso!");
						setIsPolling(false);
						if (pollIntervalRef.current) {
							clearInterval(pollIntervalRef.current);
							pollIntervalRef.current = null;
						}
					}
				},
			});
		}, 5000);

		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		};
	}, [integration?.tested, environment, id]);

	if (!integration || !integration.provider) {
		return (
			<div className="size-full flex items-center justify-center py-4 md:py-8">
				<Card className="p-6 w-full max-md:px-4 max-w-[900px] flex flex-col gap-6">
					<Heading>Integração não encontrada</Heading>
					<p className="text-muted-foreground">
						A integração solicitada não foi encontrada ou não está disponível.
					</p>
				</Card>
			</div>
		);
	}

	const providerInfo = isKnownPaymentProvider(integration.provider)
		? PaymentProviderInfo[integration.provider]
		: null;
	const webhookUrl = `${import.meta.env.VITE_SERVER_URL}/integrations/${providerInfo?.slug ?? integration.provider.toLowerCase()}/${integration.hash}`;

	const handleCopyUrl = () => {
		copyToClipboard(webhookUrl);
		toast.success("URL copiada para a área de transferência");
	};

	const products: ProductTableDef[] =
		integration.products?.map((product) => ({
			id: product.id,
			enabled: product.enabled,
			productName: product.productName || "",
			productId: product.productId || "",
			courses:
				product.accessGroup?.accessGroup?.courses?.map((c) => ({
					name: c.course.name,
				})) || [],
			spaces:
				product.accessGroup?.accessGroup?.spaces?.filter(Boolean).map((s) => ({
					name: s.name,
					icon: s.icon || "",
				})) || [],
		})) || [];

	return (
		<div className="size-full flex items-start justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[900px] flex flex-col gap-8 border rounded-md">
				<Heading>
					Integração {providerInfo?.name || integration.provider}
				</Heading>

				<div className="flex flex-col gap-3">
					<Subheading>URL Scarf</Subheading>
					<p className="text-sm text-muted-foreground">
						Copie esta URL e cole na seção de webhook do seu checkout. Ela será
						usada para receber notificações de novas vendas.
					</p>

					<InputGroup>
						<InputGroupInput value={webhookUrl} readOnly />
						<InputGroupAddon align="inline-end">
							<button type="button" onClick={handleCopyUrl}>
								<Icon name="Copy" className="size-4" />
							</button>
						</InputGroupAddon>
					</InputGroup>

					{integration.tested ? (
						<Badge className="w-fit bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/10">
							<Icon name="Check" className="size-3" />
							Conexão Ativa
						</Badge>
					) : (
						<Badge
							variant="outline"
							className="w-fit bg-yellow-500/10 text-yellow-600 border-yellow-500/20"
						>
							{isPolling ? (
								<>
									<Icon name="LoaderCircle" className="size-3 animate-spin" />
									Validando
								</>
							) : (
								<>
									<Icon name="Clock" className="size-3" />
									Aguardando Teste
								</>
							)}
						</Badge>
					)}
				</div>

				<div className="flex flex-col gap-4">
					<div className="flex flex-row items-center justify-between">
						<Subheading>
							Produtos {providerInfo?.name || integration.provider}
						</Subheading>
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								navigate({
									to: "/c/$club/configuracoes/integracoes/checkout/produto/novo",
									params: { club: club.slug },
									search: { integration: id },
								})
							}
						>
							<Icon name="Plus" />
							Novo Produto
						</Button>
					</div>

					<ProductsTable data={products} isPending={false} />
				</div>
			</div>
		</div>
	);
}
